import TableCell from "@mui/material/TableCell"
import { format } from "date-fns"

import Column from "../../../common/components/Column"
import { adminApi } from "../axios"
import AdminHeaderTableCell, {
  AdminSortableHeaderCell,
  AdminTableHeadRow,
  adminTableBodyCellSx,
} from "../components/AdminHeaderTableCell"
import AdminListToolbar from "../components/AdminListToolbar"
import AdminListingTable, {
  AdminTableRow,
} from "../components/AdminListingTable"
import AdminPageHeader from "../components/AdminPageHeader"
import AdminTruncatedCell from "../components/AdminTruncatedCell"
import AdminZohoMailboxDialogHost from "../components/AdminZohoMailboxDialogHost"
import AdminZohoMailboxRowActions from "../components/AdminZohoMailboxRowActions"
import { useAdminPaginatedQuery } from "../hooks/useAdminPaginatedQuery"
import { useAdminZohoMailboxMutations } from "../hooks/useAdminZohoMailboxMutations"

export const MAILBOX_FILTER_SPECS = {
  concierge: { param: "concierge_user_id" },
  account: { param: "owner_user_id" },
  status: { param: "status" },
}

const TOOLBAR_FILTERS = [
  { key: "concierge", label: "Concierge", type: "entity", entity: "concierge" },
  {
    key: "account",
    label: "Parent account",
    type: "entity",
    entity: "account",
    placeholder: "Type 2+ letters",
  },
  {
    key: "status",
    label: "Status",
    type: "select",
    options: [
      { value: "active", label: "Active" },
      { value: "pending", label: "Pending" },
      { value: "disabled", label: "Disabled" },
      { value: "error", label: "Error" },
    ],
  },
]

export default function AdminZohoMailbox() {
  const query = useAdminPaginatedQuery(adminApi.fetchMailboxes, {
    filters: MAILBOX_FILTER_SPECS,
    defaultSortColumn: "created",
    defaultSortDirection: "desc",
  })
  const {
    rows,
    isLoading,
    pageIndex,
    setPageIndex,
    pageSize,
    setPageSize,
    totalResultsSize,
    refresh,
  } = query

  const mutations = useAdminZohoMailboxMutations(() => refresh())

  return (
    <Column gap={2}>
      <AdminPageHeader
        title="Zoho mailbox"
        subtitle="Company Zoho mailboxes — reassign concierge / parent account in OneBase, or delete to disable the user in Zoho Mail."
      />
      <AdminListToolbar
        query={query}
        searchLabel="Search mailbox, concierge or parent account"
        noun={totalResultsSize === 1 ? "mailbox" : "mailboxes"}
        filters={TOOLBAR_FILTERS}
      />
      <AdminListingTable
        rows={rows}
        isLoading={isLoading}
        pageIndex={pageIndex}
        setPageIndex={setPageIndex}
        pageSize={pageSize}
        setPageSize={setPageSize}
        totalResultsSize={totalResultsSize}
        columnCount={9}
        tableHead={
          <AdminTableHeadRow>
            <AdminSortableHeaderCell
              columnName="email"
              label="Email"
              query={query}
              minWidth={{ xs: 88, sm: 120 }}
            />
            <AdminHeaderTableCell minWidth={{ xs: 88, sm: 112 }}>
              Display name
            </AdminHeaderTableCell>
            <AdminHeaderTableCell minWidth={{ xs: 96, sm: 120 }}>
              Assigned user
            </AdminHeaderTableCell>
            <AdminHeaderTableCell minWidth={{ xs: 96, sm: 120 }}>
              Parent account
            </AdminHeaderTableCell>
            <AdminHeaderTableCell minWidth={{ xs: 56, sm: 72 }}>
              Status
            </AdminHeaderTableCell>
            <AdminHeaderTableCell minWidth={{ xs: 56, sm: 72 }}>
              OAuth
            </AdminHeaderTableCell>
            <AdminHeaderTableCell minWidth={{ xs: 96, sm: 120 }}>
              Forwarding
            </AdminHeaderTableCell>
            <AdminHeaderTableCell minWidth={{ xs: 72, sm: 88 }}>
              Last sync
            </AdminHeaderTableCell>
            <AdminHeaderTableCell align="right" minWidth={{ xs: 64, sm: 80 }}>
              Actions
            </AdminHeaderTableCell>
          </AdminTableHeadRow>
        }
      >
        {rows.map((row) => (
          <AdminTableRow key={row.id} hover>
            <AdminTruncatedCell value={row.email} />
            <AdminTruncatedCell value={row.display_name} />
            <AdminTruncatedCell
              value={row.assigned_user_name || row.assigned_user_email}
            />
            <AdminTruncatedCell
              value={
                row.owner_user_name || row.owner_user_email
                  ? `${row.owner_user_name || ""} (${
                      row.owner_user_email || ""
                    })`.trim()
                  : "Unmapped"
              }
            />
            <AdminTruncatedCell value={row.status} />
            <AdminTruncatedCell value={row.oauth_status} />
            <AdminTruncatedCell
              value={
                row.forwarding_address
                  ? `${row.forwarding_address}${
                      row.forwarding_enabled ? "" : " (pending)"
                    }`
                  : "—"
              }
            />
            <AdminTruncatedCell
              value={
                row.last_sync_at
                  ? format(new Date(row.last_sync_at), "MMM d, h:mm a")
                  : "—"
              }
            />
            <TableCell align="right" sx={adminTableBodyCellSx}>
              <AdminZohoMailboxRowActions
                row={row}
                onEdit={mutations.setEditRow}
                onDelete={mutations.setDeleteRow}
              />
            </TableCell>
          </AdminTableRow>
        ))}
      </AdminListingTable>

      <AdminZohoMailboxDialogHost
        editRow={mutations.editRow}
        deleteRow={mutations.deleteRow}
        saving={mutations.saving}
        dialogError={mutations.dialogError}
        onClose={mutations.closeDialogs}
        onSave={mutations.handleSaveEdit}
        onConfirmDelete={mutations.handleConfirmDelete}
      />
    </Column>
  )
}
